import { AnimatePresence, motion } from "motion/react";
import { CSSProperties, FocusEvent, useMemo, useState } from "react";

type LivePreviewLinkProps = {
  href: string;
  label: string;
  anchorClassName?: string;
  anchorStyle?: CSSProperties;
  domainLabel?: string;
  iframeTitle?: string;
};

export function LivePreviewLink({
  href,
  label,
  anchorClassName,
  anchorStyle,
  domainLabel,
  iframeTitle,
}: LivePreviewLinkProps) {
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);

  const previewDomain = useMemo(() => {
    if (domainLabel && domainLabel.trim().length > 0) {
      return domainLabel;
    }

    try {
      return new URL(href).hostname.replace(/^www\./, "");
    } catch {
      return href;
    }
  }, [domainLabel, href]);

  const handleBlurCapture = (event: FocusEvent<HTMLSpanElement>) => {
    const nextTarget = event.relatedTarget as Node | null;
    if (nextTarget && event.currentTarget.contains(nextTarget)) {
      return;
    }
    setIsPreviewOpen(false);
  };

  return (
    <span
      className="group relative inline-flex"
      onMouseEnter={() => setIsPreviewOpen(true)}
      onMouseLeave={() => setIsPreviewOpen(false)}
      onFocusCapture={() => setIsPreviewOpen(true)}
      onBlurCapture={handleBlurCapture}
    >
      <a
        href={href}
        target="_blank"
        rel="noopener"
        className={anchorClassName}
        style={anchorStyle}
      >
        {label}
      </a>

      <AnimatePresence>
        {isPreviewOpen ? (
          <motion.span
            initial={{ opacity: 0, y: 6, filter: "blur(8px)" }}
            animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
            exit={{ opacity: 0, y: 2, filter: "blur(0px)" }}
            transition={{ duration: 0.2, ease: "easeOut" }}
            className="cv-pdf-hide pointer-events-none absolute bottom-full left-1/2 z-40 mb-2 hidden w-[320px] -translate-x-1/2 overflow-hidden rounded-lg border lg:block"
            style={{
              backgroundColor: "rgb(var(--bg-primary))",
              borderColor: "rgb(var(--border-color))",
            }}
          >
            <span
              className="relative block h-[186px] w-full overflow-hidden"
              style={{ backgroundColor: "rgb(var(--bg-secondary))" }}
            >
              <iframe
                title={iframeTitle ?? `${label} live preview`}
                src={href}
                loading="lazy"
                aria-hidden="true"
                className="pointer-events-none h-[820px] w-[1400px] scale-[0.228] origin-top-left border-0"
                referrerPolicy="no-referrer"
              />
            </span>

            <span
              className="flex items-center justify-between border-t px-2 py-1 text-[11px]"
              style={{
                borderColor: "rgb(var(--border-color))",
                color: "rgb(var(--text-secondary))",
              }}
            >
              <span className="max-w-[220px] truncate">{previewDomain}</span>
              <span className="tracking-[0.08em] uppercase">Live preview</span>
            </span>
          </motion.span>
        ) : null}
      </AnimatePresence>
    </span>
  );
}
